import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import Cookies from "js-cookie"
import { organizationApi } from "../../Apis/organizationApi"
import {
  parseOrganizationMembersResponse,
  unwrapOrganizationResult,
} from "../../utils/organizationApiHelpers"
import {
  profileOrgOwnerFlag,
  shouldSkipOrganizationProbe,
} from "../../utils/organizationAccess"
import { fetchUserDetails } from "./UserSlice"

function applyProfileOrgOwnerFlag(state, profile) {
  const flag = profileOrgOwnerFlag(profile)
  if (flag === false) {
    state.isOwner = false
    state.overview = null
    state.members = []
    return
  }
  if (flag === true) {
    state.isOwner = true
  }
}

export const probeOrganizationOwner = createAsyncThunk(
  "organization/probeOwner",
  async (profileOverride, { rejectWithValue, getState }) => {
    const orgState = getState()?.organization
    const user = profileOverride ?? getState()?.user?.user
    const orgOwnerFlag = profileOrgOwnerFlag(user)

    if (orgOwnerFlag !== null) {
      return {
        isOwner: orgOwnerFlag,
        overview: orgOwnerFlag ? orgState?.overview ?? null : null,
      }
    }

    if (orgState?.isOwner !== null && profileOverride === undefined) {
      return { isOwner: orgState.isOwner, overview: orgState.overview }
    }

    const token = Cookies.get("token")

    if (shouldSkipOrganizationProbe(user, token)) {
      return { isOwner: false, overview: null }
    }

    try {
      const response = await organizationApi.getOverview()
      const result = unwrapOrganizationResult(response)
      if (result?.organization) {
        return { isOwner: true, overview: result }
      }
      return { isOwner: false, overview: null }
    } catch (error) {
      const status = error?.response?.status
      if (status >= 400 && status < 500) {
        return { isOwner: false, overview: null }
      }
      return rejectWithValue(error?.response?.data || "probe_failed")
    }
  },
)

export const fetchOrganizationOverview = createAsyncThunk(
  "organization/fetchOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await organizationApi.getOverview()
      return unwrapOrganizationResult(response)
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  },
)

export const fetchOrganizationMembers = createAsyncThunk(
  "organization/fetchMembers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await organizationApi.getMembers()
      return parseOrganizationMembersResponse(response)
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  },
)

const initialState = {
  isOwner: null,
  overview: null,
  members: [],
  probeLoading: false,
  overviewLoading: false,
  membersLoading: false,
  error: null,
}

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    clearOrganizationState: () => initialState,
    setPoolUnallocated: (state, action) => {
      if (state.overview?.subscription) {
        state.overview.subscription.unallocated_images = action.payload
      }
    },
    setPoolUnallocatedFurnitureFinder: (state, action) => {
      if (state.overview?.subscription) {
        state.overview.subscription.unallocated_furniture_finder = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(probeOrganizationOwner.pending, (state) => {
        state.probeLoading = true
      })
      .addCase(probeOrganizationOwner.fulfilled, (state, action) => {
        state.probeLoading = false
        state.isOwner = action.payload.isOwner
        if (action.payload.overview) {
          state.overview = action.payload.overview
        }
      })
      .addCase(probeOrganizationOwner.rejected, (state) => {
        state.probeLoading = false
        state.isOwner = false
      })
      .addCase(fetchOrganizationOverview.pending, (state) => {
        state.overviewLoading = true
      })
      .addCase(fetchOrganizationOverview.fulfilled, (state, action) => {
        state.overviewLoading = false
        state.overview = action.payload
      })
      .addCase(fetchOrganizationOverview.rejected, (state, action) => {
        state.overviewLoading = false
        state.error = action.payload
      })
      .addCase(fetchOrganizationMembers.pending, (state) => {
        state.membersLoading = true
      })
      .addCase(fetchOrganizationMembers.fulfilled, (state, action) => {
        state.membersLoading = false
        state.members = action.payload
      })
      .addCase(fetchOrganizationMembers.rejected, (state, action) => {
        state.membersLoading = false
        state.error = action.payload
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        applyProfileOrgOwnerFlag(state, action.payload)
      })
  },
})

export const {
  clearOrganizationState,
  setPoolUnallocated,
  setPoolUnallocatedFurnitureFinder,
} = organizationSlice.actions
export default organizationSlice.reducer
